import React, { useContext, useMemo, useRef } from "react"
import { get, useFormContext, useFormState } from "react-hook-form"

import classNames from "classnames"

import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"

import {
  Input as DefaultInput,
  InputChangeBehavior,
  InputContext,
  InputRootProps
} from "../Input"
import { InputFocus } from "../InputFocus"
import { useInputDescriptionIDs } from "../accessibility/useInputAriaDescription"
import { useInputChangeBehavior } from "../hooks/useInputChangeBehavior"
import { TextInputContext, TextInputContextType } from "./TextInputContext"
import { ReadonlyText } from "./components/ReadonlyText"
import { TextInputCounter } from "./components/TextInputCounter"
import { TextInputErrorMessage } from "./components/TextInputErrorMessage"

export interface TextInputRootProps
  extends InputRootProps,
    TextInputContextType {}

export { TextInputContext } from "./TextInputContext"

export interface TextInputProps extends React.HTMLProps<HTMLInputElement> {
  changeBehavior?: InputChangeBehavior
}

interface TextInputClearProps {
  className?: string
  onChange?: (value: string) => void
  value?: string
}

const HookFormTextInputClear = React.memo(
  React.forwardRef<HTMLButtonElement, TextInputClearProps>(
    ({ className, onChange, value: valueProp }, ref) => {
      const { name } = React.useContext(InputContext)
      const formContext = useFormContext()

      const formValue = name ? formContext.watch(name) : undefined
      const value = valueProp ?? formValue

      const handleClear = useEvent(() => {
        if (formContext && name) {
          formContext.setValue(name, "", {
            shouldValidate: true,
            shouldDirty: true
          })
        }

        if (onChange) {
          onChange("")
        }
      })

      return (
        <DefaultInput.Clear
          onClick={handleClear}
          value={value}
          shouldHide
          className={className}
          ref={ref}
        />
      )
    }
  )
)
HookFormTextInputClear.displayName = "HookFormTextInputClear"

const BaseTextInputClear = React.memo(
  React.forwardRef<HTMLButtonElement, TextInputClearProps>(
    ({ className, onChange, value }, ref) => {
      const handleClear = useEvent(() => {
        if (onChange) {
          onChange("")
        }
      })

      return (
        <DefaultInput.Clear
          onClick={handleClear}
          value={value}
          shouldHide
          className={className}
          ref={ref}
        />
      )
    }
  )
)
BaseTextInputClear.displayName = "BaseTextInputClear"

const TextInputClear = React.memo(
  React.forwardRef<HTMLButtonElement, TextInputClearProps>((props, ref) => {
    const { name } = React.useContext(InputContext)

    if (name) {
      return <HookFormTextInputClear {...props} ref={ref} />
    }
    return <BaseTextInputClear {...props} ref={ref} />
  })
)
TextInputClear.displayName = "TextInputClear"

const Root = React.memo(
  React.forwardRef(
    (
      { maxLength, minLength, className, ...props }: TextInputRootProps,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      const context = useMemo(
        () => ({ maxLength, minLength }),
        [maxLength, minLength]
      )
      return (
        <TextInputContext.Provider value={context}>
          <DefaultInput.Root
            {...props}
            className={classNames("text-input", className)}
            ref={ref}
          />
        </TextInputContext.Provider>
      )
    }
  )
)

const InputBase = React.memo(
  React.forwardRef(
    (
      {
        type,
        onChange,
        onBlur,
        onKeyDown,
        changeBehavior = InputChangeBehavior.CHANGE,
        ...rest
      }: TextInputProps,
      externalRef: React.ForwardedRef<HTMLInputElement>
    ) => {
      const internalRef = useRef<HTMLInputElement>(null)
      const { inputRef } = useContext(InputContext)
      const { ariaDescribedBy } = useInputDescriptionIDs({
        additionalDescriptions: rest["aria-describedby"]
      })

      const { id, autoFocus, label, disabled } = useContext(InputContext)
      const { minLength, maxLength } = useContext(TextInputContext)

      useAutoFocus({ autoFocus, ref: internalRef })

      const ref = useMergedRefs(
        externalRef,
        internalRef,
        inputRef as React.Ref<HTMLInputElement>
      )
      const { handleChange, handleBlur, handleKeyDown } =
        useInputChangeBehavior({
          onChange,
          onBlur,
          onKeyDown,
          changeBehavior
        })

      return (
        <input
          id={id}
          type={type ?? "text"}
          aria-label={label}
          minLength={minLength}
          maxLength={maxLength}
          disabled={disabled}
          {...rest}
          aria-describedby={ariaDescribedBy}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          ref={ref}
        />
      )
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, type, ...rest }: TextInputProps,
      externalRef: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { name, required, disabled } = useContext(InputContext)
      const { minLength, maxLength } = useContext(TextInputContext)
      const { register } = useFormContext()
      const { errors } = useFormState({ name })
      const error = get(errors, name)

      const registerProps = register(name, {
        required,
        disabled,
        minLength,
        maxLength
      })

      const ref = useMergedRefs(externalRef, registerProps.ref)

      return (
        <InputBase
          className={classNames({ error: !!error }, className) || undefined}
          {...registerProps}
          {...rest}
          ref={ref}
        />
      )
    }
  )
)

const Input = React.memo(
  React.forwardRef(
    (props: TextInputProps, externalRef: React.Ref<HTMLInputElement>) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <InputBase {...props} ref={externalRef} />
    }
  )
)

const TextInput = {
  ...DefaultInput,
  Root,
  Focus: InputFocus,
  Input,
  Clear: TextInputClear,
  ErrorMessage: TextInputErrorMessage,
  Readonly: ReadonlyText,
  Counter: TextInputCounter
}

export { TextInput }
